import { apply, bump, counter, toast } from '../core/effects';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { BlocksGame, COLS, PIECES, ROWS } from '../minigames/blocks';
import { h } from './dom';
import { isPanelOpen, openPanel, refreshPanel } from './panel';

const CELL = 18;
const DAILY_STAR_CAP = 30;

let game = new BlocksGame();
let raf = 0;
let last = 0;
let acc = 0;
let canvas: HTMLCanvasElement | null = null;
let flash = 0;
let paused = false;

export function openBlocks(): void {
  game = new BlocksGame();
  game.onClear = onClear;
  paused = false;
  openPanel('blocks', render, stop);
  start();
}

function render() {
  const t = m().blocks;
  canvas = h('canvas', { class: 'blocks-cv', width: COLS * CELL * 2, height: ROWS * CELL * 2 });
  const hold = (label: string, fn: () => void, cls = '') => {
    let timer = 0;
    const stopRepeat = () => window.clearInterval(timer);
    return h(
      'button',
      {
        class: `btn pad ${cls}`,
        'aria-label': label,
        onpointerdown: (e: PointerEvent) => {
          e.preventDefault();
          fn();
          stopRepeat();
          timer = window.setTimeout(() => (timer = window.setInterval(fn, 60)), 180);
        },
        onpointerup: stopRepeat,
        onpointerleave: stopRepeat,
        onpointercancel: stopRepeat,
      },
      label,
    );
  };
  return {
    title: t.title,
    className: 'blocks',
    body: [
      h(
        'div',
        { class: 'blocks-top' },
        h('div', null, h('small', null, t.score), h('b', { id: 'bkScore' }, String(game.score))),
        h('div', null, h('small', null, t.lines), h('b', { id: 'bkLines' }, String(game.lines))),
        h('div', null, h('small', null, t.level), h('b', { id: 'bkLevel' }, String(game.level))),
        h('div', null, h('small', null, t.next), h('canvas', { id: 'bkNext', width: 72, height: 48 })),
      ),
      h('div', { class: 'blocks-wrap' }, canvas),
      h(
        'div',
        { class: 'blocks-pad' },
        hold('◀', () => act('left')),
        hold('⟳', () => act('rotate')),
        hold('▶', () => act('right')),
        hold('▼', () => act('down')),
        hold('⤓', () => act('drop'), 'red'),
      ),
    ],
    footer: [h('button', { class: 'btn green', onclick: restart }, t.restart)],
  };
}

function restart(): void {
  game.reset();
  sfx('pop');
  refreshPanel('blocks');
  start();
}

export function act(a: 'left' | 'right' | 'rotate' | 'down' | 'drop'): void {
  if (game.over || paused) return;
  if (a === 'left' && game.move(-1)) sfx('click');
  if (a === 'right' && game.move(1)) sfx('click');
  if (a === 'rotate' && game.rotate()) sfx('pop');
  if (a === 'down') {
    game.softDrop(true);
    acc = 0;
  }
  if (a === 'drop') {
    sfx('drop');
    game.hardDrop();
    acc = 0;
  }
  if (game.over) gameOver();
  draw();
}

/** Keyboard support while the panel is open. Returns true if handled. */
export function blocksKey(e: KeyboardEvent): boolean {
  if (!isPanelOpen('blocks')) return false;
  const map: Record<string, Parameters<typeof act>[0]> = {
    ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
    ArrowUp: 'rotate', KeyW: 'rotate', KeyX: 'rotate', ArrowDown: 'down', KeyS: 'down', Space: 'drop',
  };
  const a = map[e.code];
  if (!a) return false;
  e.preventDefault();
  act(a);
  return true;
}

function onClear(n: number): void {
  flash = 1;
  const t = m().blocks;
  const stars = n === 4 ? 15 : n * 3;
  const room = Math.max(0, DAILY_STAR_CAP - counter('blockStars'));
  const got = Math.min(room, stars);
  if (n === 4) {
    sfx('fanfare');
    toast(t.quad);
  } else {
    sfx('line');
    toast(t.cleared(n));
  }
  if (got > 0) {
    bump('blockStars', got);
    apply({ stars: got }, { float: false });
  } else if (counter('blockStars') >= DAILY_STAR_CAP && !counter('blockCapNote')) {
    bump('blockCapNote');
    toast(t.capped);
  }
}

function gameOver(): void {
  sfx('bad');
  toast(m().blocks.final(game.score));
  draw();
}

function start(): void {
  cancelAnimationFrame(raf);
  last = performance.now();
  acc = 0;
  const loop = (now: number) => {
    if (!isPanelOpen('blocks')) return;
    const dt = Math.min(100, now - last);
    last = now;
    if (!game.over && !paused) {
      acc += dt;
      if (acc >= game.dropInterval) {
        acc = 0;
        game.softDrop();
        if (game.over) gameOver();
      }
    }
    flash = Math.max(0, flash - dt / 300);
    draw();
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
}

function stop(): void {
  cancelAnimationFrame(raf);
  canvas = null;
}

/** Pause the falling piece when the tab is hidden. */
document.addEventListener('visibilitychange', () => {
  paused = document.hidden;
});

function cell(c: CanvasRenderingContext2D, x: number, y: number, col: string, size: number): void {
  c.fillStyle = col;
  c.fillRect(x + 1, y + 1, size - 2, size - 2);
  c.fillStyle = 'rgba(255,255,255,0.35)';
  c.fillRect(x + 1, y + 1, size - 2, 3);
  c.fillRect(x + 1, y + 1, 3, size - 2);
  c.fillStyle = 'rgba(0,0,0,0.3)';
  c.fillRect(x + 1, y + size - 4, size - 2, 3);
  c.fillRect(x + size - 4, y + 1, 3, size - 2);
}

function draw(): void {
  if (!canvas) return;
  const c = canvas.getContext('2d')!;
  const S = CELL * 2;
  c.fillStyle = '#150f0c';
  c.fillRect(0, 0, canvas.width, canvas.height);
  c.strokeStyle = 'rgba(255,255,255,0.04)';
  for (let x = 0; x <= COLS; x++) c.strokeRect(x * S, 0, 0, canvas.height);
  for (let y = 0; y <= ROWS; y++) c.strokeRect(0, y * S, canvas.width, 0);
  game.board.forEach((row, r) => row.forEach((v, col) => v && cell(c, col * S, r * S, v, S)));
  const p = game.current;
  if (p) {
    const gy = game.ghostY();
    c.globalAlpha = 0.22;
    p.shape.forEach((row, r) => row.forEach((v, col) => v && cell(c, (p.x + col) * S, (gy + r) * S, p.color, S)));
    c.globalAlpha = 1;
    p.shape.forEach((row, r) => row.forEach((v, col) => v && p.y + r >= 0 && cell(c, (p.x + col) * S, (p.y + r) * S, p.color, S)));
  }
  if (flash > 0) {
    c.fillStyle = `rgba(255,240,180,${flash * 0.35})`;
    c.fillRect(0, 0, canvas.width, canvas.height);
  }
  if (game.over) {
    c.fillStyle = 'rgba(10,6,4,0.72)';
    c.fillRect(0, 0, canvas.width, canvas.height);
    c.fillStyle = '#ffd84a';
    c.font = '800 34px system-ui, sans-serif';
    c.textAlign = 'center';
    c.fillText(m().blocks.over, canvas.width / 2, canvas.height / 2 - 10);
    c.font = '600 22px system-ui, sans-serif';
    c.fillStyle = '#fff3d0';
    c.fillText(m().blocks.final(game.score), canvas.width / 2, canvas.height / 2 + 26);
  }
  const set = (id: string, v: number) => {
    const el = document.getElementById(id);
    if (el && el.textContent !== String(v)) el.textContent = String(v);
  };
  set('bkScore', game.score);
  set('bkLines', game.lines);
  set('bkLevel', game.level);
  const nx = document.getElementById('bkNext') as HTMLCanvasElement | null;
  if (nx) {
    const n = nx.getContext('2d')!;
    n.clearRect(0, 0, nx.width, nx.height);
    const piece = PIECES[game.nextKey];
    const sz = 12;
    const rows = piece.shape.filter((r) => r.some(Boolean));
    const w = piece.shape[0].length * sz;
    rows.forEach((row, r) => row.forEach((v, col) => v && cell(n, (nx.width - w) / 2 + col * sz, (nx.height - rows.length * sz) / 2 + r * sz, piece.color, sz)));
  }
}
